import type { CommandOf, CommandResult } from '@/core/app/commands';
import type { FlagId, UnlockId } from '@/core/content/ids';
import type { ContentRegistry } from '@/core/content/registry';
import type { DomainEvent } from '@/core/events/types';
import type { GameSave } from '@/core/persistence/schema/v1';
import { evaluateAll, type MissingReason } from './conditions';
import type { HandlerEnv } from './placement/handlers';
import { grantRewards } from './rewards';

export type UnlockStatus =
  | { kind: 'granted' }
  | { kind: 'purchasable'; cost: number; affordable: boolean }
  | { kind: 'locked'; missing: MissingReason[] };

/** Derived, never stored (content-schemas §4.1). */
export function unlockStatus(content: ContentRegistry, s: GameSave, id: UnlockId): UnlockStatus {
  if (s.unlockedIds.includes(id)) return { kind: 'granted' };
  const def = content.unlocks.get(id);
  if (!def) return { kind: 'locked', missing: [] };
  const auto = evaluateAll(content, s, def.prerequisites);
  if (!auto.ok) return { kind: 'locked', missing: auto.missing };
  const cost = def.prerequisites.find((c) => c.type === 'coinsCost');
  if (cost?.type === 'coinsCost') {
    return { kind: 'purchasable', cost: cost.amount, affordable: s.wallet.coins >= cost.amount };
  }
  return { kind: 'locked', missing: [] };
}

/** Apply an unlock's grants exactly once (unlockedIds is a set). Never revoked. */
export function grantUnlock(
  draft: GameSave,
  content: ContentRegistry,
  id: UnlockId,
  paid: boolean,
  cost?: number,
): DomainEvent[] {
  if (draft.unlockedIds.includes(id)) return [];
  const def = content.unlocks.get(id);
  draft.unlockedIds.push(id);
  const events: DomainEvent[] = [];
  def?.grants.forEach((g, i) => {
    switch (g.type) {
      case 'scene':
        if (!draft.scenes.includes(g.sceneId)) draft.scenes.push(g.sceneId);
        break;
      case 'feature':
        if (!draft.features.includes(g.feature)) draft.features.push(g.feature);
        break;
      case 'chapter':
        if (!draft.story.unlockedChapters.includes(g.chapterId)) {
          draft.story.unlockedChapters.push(g.chapterId);
          events.push({ type: 'CHAPTER_UNLOCKED', chapterId: g.chapterId });
        }
        break;
      case 'roomExpansion': {
        const room = draft.rooms[g.roomId];
        if (!room.expansions.includes(g.expansionId)) room.expansions.push(g.expansionId);
        break;
      }
      case 'flag':
        draft.flags[g.flag] = g.value;
        break;
      case 'reward':
        events.push(...grantRewards(draft, content, `unlock:${id}:${String(i)}`, [g.reward]));
        break;
    }
  });
  events.push({ type: 'UNLOCK_GRANTED', unlockId: id, paid, ...(cost !== undefined ? { cost } : {}) });
  return events;
}

/**
 * Pipeline step f (content-schemas §14.6): grant every unlock whose automatic prerequisites hold.
 * Paid unlocks become "purchasable" and wait for the player — money is never taken automatically.
 */
export function unlockSystem(
  draft: GameSave,
  _events: readonly DomainEvent[],
  env: HandlerEnv,
): DomainEvent[] {
  return evaluateUnlocks(draft, env.content);
}

/** Set once a paid unlock has been offered (S15 "Mở rộng với 300 xu" shows only the first time). */
export const offerFlag = (id: UnlockId): FlagId => `flag_offered_${id}`;

export function evaluateUnlocks(draft: GameSave, content: ContentRegistry): DomainEvent[] {
  const out: DomainEvent[] = [];
  let changed = true;
  for (let guard = 0; changed && guard < 5; guard++) {
    changed = false;
    for (const id of content.unlockTopo) {
      if (draft.unlockedIds.includes(id)) continue;
      const def = content.unlocks.get(id);
      if (!def) continue;
      const cost = def.prerequisites.find((c) => c.type === 'coinsCost');
      if (cost?.type === 'coinsCost') {
        // Money is never taken automatically: offer it once, the player decides.
        if (draft.flags[offerFlag(id)] || !evaluateAll(content, draft, def.prerequisites).ok) continue;
        draft.flags[offerFlag(id)] = true;
        out.push({ type: 'UNLOCK_OFFERED', unlockId: id, cost: cost.amount });
        continue;
      }
      if (!evaluateAll(content, draft, def.prerequisites).ok) continue;
      out.push(...grantUnlock(draft, content, id, false));
      changed = true;
    }
  }
  return out;
}

/** The player chooses to pay for a purchasable unlock (e.g. shop expansion). */
export function payUnlock(draft: GameSave, cmd: CommandOf<'payUnlock'>, env: HandlerEnv): CommandResult {
  const status = unlockStatus(env.content, draft, cmd.unlockId);
  if (status.kind === 'granted') return { ok: false, error: 'ALREADY_CLAIMED' };
  if (status.kind === 'locked')
    return { ok: false, error: 'CONDITIONS_NOT_MET', detail: { missing: status.missing } };
  if (!status.affordable) return { ok: false, error: 'INSUFFICIENT_COINS', detail: { cost: status.cost } };
  draft.wallet.coins -= status.cost;
  env.ledger?.push({
    txId: `${draft.saveId}:unlock:${cmd.unlockId}`,
    saveId: draft.saveId,
    atWallMs: env.nowWallMs,
    atPlayMs: draft.clock.playTimeMs,
    kind: 'unlockPayment',
    coinsDelta: -status.cost,
    balanceAfter: draft.wallet.coins,
    refs: { unlockId: cmd.unlockId },
  });
  const events: DomainEvent[] = [
    {
      type: 'COINS_CHANGED',
      delta: -status.cost,
      balance: draft.wallet.coins,
      reason: 'unlockPayment',
      ref: cmd.unlockId,
    },
  ];
  events.push(...grantUnlock(draft, env.content, cmd.unlockId, true, status.cost));
  return { ok: true, events };
}
